import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight, ArrowDownRight, Scale, Landmark, Wallet } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { fmt } from "@/lib/format";
import { Money, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

function Kpi({ label, value, icon: Icon, tone, testid, delay }) {
  const tones = { green: "text-[#007A3D] bg-emerald-50", red: "text-[#CE1126] bg-red-50", gold: "text-[#9A7B1C] bg-[#FFFBEB]", dark: "text-slate-900 bg-slate-100" };
  return (
    <Panel className="rise p-5 transition-transform hover:-translate-y-0.5" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
        <span className={`rounded-md p-1.5 ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <Money testid={testid} value={value} className="mt-4 block text-2xl font-semibold text-slate-900" />
    </Panel>
  );
}

export default function Dashboard() {
  const { t, accName, locale } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-400">{t("loading")}</div>;
  const month = (m) => new Date(`${m}-01`).toLocaleDateString(locale, { month: "short" });
  const maxTop = Math.max(...d.top_expenses.map((x) => x.balance), 1);

  return (
    <div data-testid="dashboard-page">
      <div className="rise relative mb-8 overflow-hidden rounded-2xl bg-[#0B1220] p-7 text-white sm:p-9">
        <div className="absolute inset-y-0 start-0 w-2 bg-[#CE1126]" />
        <div className="absolute -end-10 -top-16 h-56 w-56 rounded-full bg-[#007A3D]/30 blur-3xl" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">{t("ytd")}</div>
            <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">{t("welcome")}, {user?.name}</h1>
            <p className="mt-2 text-sm text-white/60">{t("receivables")}: <Money value={d.receivables} testid="kpi-receivables" className="text-white" /></p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild data-testid="dash-new-invoice-btn" className="bg-[#007A3D] hover:bg-[#00954A]"><Link to="/invoices/new"><Plus className="me-1 h-4 w-4" />{t("newInvoice")}</Link></Button>
            <Button asChild variant="outline" data-testid="dash-new-expense-btn" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"><Link to="/expenses"><Plus className="me-1 h-4 w-4" />{t("newExpense")}</Link></Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label={t("revenueYtd")} value={d.revenue} icon={ArrowUpRight} tone="green" testid="kpi-revenue" delay={0} />
        <Kpi label={t("expensesYtd")} value={d.expenses} icon={ArrowDownRight} tone="red" testid="kpi-expenses" delay={60} />
        <Kpi label={t("netProfit")} value={d.profit} icon={Scale} tone="dark" testid="kpi-profit" delay={120} />
        <Kpi label={t("vatPayable")} value={d.vat_payable} icon={Landmark} tone="gold" testid="kpi-vat" delay={180} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel className="p-6 lg:col-span-2">
          <h3 className="mb-5 font-display text-lg font-bold">{t("monthlyOverview")}</h3>
          <div className="h-72" dir="ltr" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={d.monthly} barGap={4}>
                <CartesianGrid vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="month" tickFormatter={month} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${v / 1000}k`} tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `AED ${fmt(v)}`} labelFormatter={month} cursor={{ fill: "#F1F5F9" }} />
                <Bar dataKey="revenue" name={t("revenue")} fill="#007A3D" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name={t("expensesLabel")} fill="#CE1126" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel className="p-6">
          <h3 className="mb-5 font-display text-lg font-bold">{t("topExpenses")}</h3>
          <div className="space-y-4">
            {d.top_expenses.length === 0 && <p className="text-sm text-slate-400">{t("noData")}</p>}
            {d.top_expenses.map((x) => (
              <div key={x.id}>
                <div className="mb-1.5 flex justify-between gap-2 text-sm"><span className="truncate text-slate-700">{accName(x)}</span><Money value={x.balance} cur={false} className="text-slate-900" /></div>
                <div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[#CE1126]" style={{ width: `${(x.balance / maxTop) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel className="mt-6 overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="font-display text-lg font-bold">{t("recentInvoices")}</h3>
          <Link to="/invoices" className="text-sm font-semibold text-[#007A3D] hover:underline">{t("invoices")} →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table mt-3">
            <tbody>
              {d.recent_invoices.length === 0 && <tr><td className="py-8 text-center text-slate-400"><Wallet className="mx-auto mb-2 h-5 w-5" />{t("noData")}</td></tr>}
              {d.recent_invoices.map((i) => (
                <tr key={i.id} data-testid={`recent-invoice-${i.number}`}>
                  <td><Link to={`/invoices/${i.id}`} className="font-mono font-semibold text-slate-900 hover:text-[#007A3D]">{i.number}</Link></td>
                  <td>{i.client_name}</td>
                  <td className="font-mono text-slate-500">{i.issue_date}</td>
                  <td><StatusBadge status={i.status} /></td>
                  <td className="text-end"><Money value={i.total} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
